import { Button, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ThoughtCard } from "../routes/exercises/thoughts";
import {
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { DndProvider, useDrag, useDragLayer, useDrop } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";

const Card: React.FC<{ feeling: string; canDrag: boolean; onDelete?: ()=>void }> = ({ feeling, canDrag, onDelete }) => {
  const theme = useTheme();
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'CARD',
    item: {feeling},
    canDrag: canDrag,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <Stack
      ref={drag}
      direction={'row'}
      alignItems={'center'}
      sx={{
        padding: 1,
        margin: 0.5,
        borderRadius: '5px',
        backgroundColor: theme.palette.primary.main,
        opacity: 0.8
      }}
    >
      <Typography variant='body2' flexGrow={1} sx={{color: theme.palette.primary.contrastText}}>{feeling}</Typography>
      <IconButton
        disabled={onDelete === undefined ? true: false}
        sx={{visibility: onDelete === undefined ? 'hidden' : 'visible',
          color: theme.palette.primary.contrastText
        }}
        size='small'
        onClick={onDelete}
      >
        <CancelIcon/>
      </IconButton>
    </Stack>
  );
};

const CustomDragLayer: React.FC = () => {
  const {
    isDragging,
    item,
    initialOffset,
    currentOffset,
  } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging) {
    return null;
  }

  const layerStyles: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 1000,
    left: 0,
    top: 0,
    opacity: 0.8,
  };

  const getItemStyles = (initialOffset: any, currentOffset: any) => {
    if (!initialOffset || !currentOffset) {
      return {
        display: 'none',
      };
    }

    const { x, y } = currentOffset;
    const transform = `translate(${x}px, ${y}px)`;
    return {
      transform,
      WebkitTransform: transform,
    };
  };

  return (
    <div style={layerStyles}>
      <div style={getItemStyles(initialOffset, currentOffset)}>
        <Card feeling={item.feeling} canDrag={false}></Card>
      </div>
    </div>
  );
};

const DropZone: React.FC<{
    cards?: string[];
    onDrop: (item: {feeling: string}) => void;
    onDelete: (card : string) => void
  }> = ({ cards, onDrop, onDelete }) => {
  const [, drop] = useDrop(() => ({
    accept: 'CARD',
    drop: (item: {feeling: string}) => onDrop(item)
  }));

  return (
    <Stack
      ref={drop}
      flexGrow={1}
      spacing={2}
      p={2}
      alignItems={'center'}
      width={'100%'}
      minHeight={'100px'}
      sx={{
        border: `1px dotted grey`,
        boxSizing: 'border-box',
        borderRadius: '20px',
      }}
    >
      <Typography>This is what it makes me feel:</Typography>
      {
        cards && cards.length > 0 && (
          <Grid container>
              { cards.map((card, index) => (
                <Grid item sm={4} xs={12} key={index}>
                  <Card feeling={card} canDrag={false} onDelete={()=>onDelete(card)}></Card>
                </Grid>
              ))}
          </Grid>
        )
      }
    </Stack>
  );
};

interface ThoughtFeelingsPanelProps {
  card: ThoughtCard
  feelings: string[]
  onSetFeeling?: (feeling: string) => void
  onRemoveFeeling?: ( ) => void
  onNext?: () => void
}

function ThoughtFeelingsPanel( props: ThoughtFeelingsPanelProps ) {
  const onDrop = (item: { feeling: string} ) => {
    if (props.onSetFeeling !== undefined)
      props.onSetFeeling(item.feeling)
  };

  const onDelete = ( feeling: string ) => {
    if (props.onRemoveFeeling !== undefined)
       props.onRemoveFeeling()
  }

  return (
    <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
      <CustomDragLayer />
      <Stack spacing={2} px={1} alignItems={'center'}>
        <DropZone
          onDrop={onDrop}
          onDelete={onDelete}
          cards={props.card.feeling === undefined ? [] : [props.card.feeling]}
        />

        {/* Pool of cards */}
        <Grid container spacing={0}
          minHeight={'100px'}
          width={'100%'}
          sx={{
            border: `1px dotted grey`,
            boxSizing: 'border-box',
            borderRadius: '20px',
            padding: 2,
          }}
        >
          {props.feelings !== undefined && props.feelings.map((feeling) => (
            <Grid item sm={4} xs={12} key={feeling}>
              <Card feeling={feeling} key={feeling} canDrag={true}/>
            </Grid>
          ))}
        </Grid>

        <Button sx={{px: 6}} variant="contained" disabled={props.card.feeling === undefined} onClick={props.onNext}>Next</Button>
      </Stack>
    </DndProvider>
  )
}

export default ThoughtFeelingsPanel;
